// Legendary challenges: huge goals (Everesting, 24 hours around the Observatoire) on a dedicated track.
// A recording is capped at a day (MAX_ACTIVITY_S), so a challenge adds up several rides inside a time window: the attempt
// starts with the first ride and the window then runs on the clock, breaks included. Progress is computed
// from the samples and the track geometry, never from a figure sent by the client.
import { altitudeAt, buildTrack, type TrackDef } from './track.js';
import { MAX_ACTIVITY_S, type Samples } from './stats.js';
import { OBSERVATOIRE_ALT0, OBSERVATOIRE_PTS } from './tracks/observatoire.js';

/** What the goal measures: elevation gained (m), riding time (s) or distance (m). */
export type ChallengeGoal = 'climb' | 'time' | 'distance';

export interface ChallengeDef {
  id: string; name: string; desc: string; icon: string;
  /** Dedicated track, kept out of the regular track list (a 24-hour loop is no place for bots and items). */
  track: TrackDef & { id: string };
  goal: ChallengeGoal;
  /** Target in the goal's unit (m or s). */
  target: number;
  /** Time allowed from the start of the first ride (s): a ride counts if it starts inside the window. */
  window: number;
  /** XP for each completion (the first one also unlocks the challenge's achievement). */
  xp: number;
  /** Attribution of the real-world data the track and its scenery come from (shown with the challenge). */
  credit?: string;
}

/** Progress of the attempt in progress. `start` and `last`: start of the first ride and end of the last one (ms). */
export interface ChallengeAttempt { start: number; last: number; climb: number; time: number; distance: number; rides: number }
/** Per-challenge record kept in the player's progress. `best`: shortest completed attempt (s, window clock). */
export interface ChallengeRecord { attempt: ChallengeAttempt | null; completions: number; best: number | null }
export type ChallengeState = Record<string, ChallengeRecord>;
/** What one ride brings to a challenge. */
export interface ChallengeRide { climb: number; time: number; distance: number }

/** Longest ride a session records before ending by itself: just under the activity limit, so the save is never refused. */
export const CHALLENGE_SESSION_MAX = MAX_ACTIVITY_S - 120;

// The road to Everest base camp: a serpentine climb of about 600 m (around 7%), a short plateau,
// then a descent down another valley. About fifteen laps make an Everesting.
function everestRoad(): number[][] {
  const steep = [0.8, 1, 1.2, 1, 1.1, 0.9, 1.3, 1, 0.8, 1.2, 1, 1.1, 0.9, 0.7], sum = steep.reduce((a, b) => a + b, 0), TOP = 600;
  const pts: number[][] = [[200, 0, 0]];
  let alt = 0;
  steep.forEach((f, i) => { alt += (TOP * f) / sum; pts.push([200 + (i % 2 ? 180 : -180), (i + 1) * 500, Math.round(alt * 10) / 10]); });
  pts.push([-150, 7450, TOP + 8], [-700, 7400, TOP + 4]);
  for (let i = 0; i < 14; i++) pts.push([-1000 + (i % 3 === 1 ? 120 : i % 3 === 2 ? -120 : 0), 7000 - i * 500, Math.round(TOP * (1 - (i + 1) / 15) * 10) / 10]);
  pts.push([-750, -450, 0], [-250, -400, 0]);
  return pts;
}

export const CHALLENGES: ChallengeDef[] = [
  {
    id: 'everest', name: 'Everesting', icon: '🏔️',
    desc: 'Climb 8,848 m, the height of Everest, on the hairpins of the road to base camp. 36 hours to get there.',
    track: { id: 'everest-road', name: 'Road to Everest', desc: '19.6 km · a 600 m serpentine climb at 4,300 m above sea level', alt0: 4300, pts: everestRoad() },
    goal: 'climb', target: 8848, window: 36 * 3600, xp: 2000,
  },
  {
    id: 'observatoire', name: '24 hours of the Observatoire', icon: '🔭',
    desc: 'Ride 24 hours around the Royal Observatory in Uccle, Brussels, on the Avenue Circulaire loop. 36 hours on the clock, breaks included.',
    // the real road (OpenStreetMap) and its real elevation (EU-DEM): see scripts/scenery/observatoire.mjs
    track: { id: 'observatoire', name: 'Avenue Circulaire', desc: '1.26 km · the loop around the Royal Observatory, 7 m of rise', alt0: OBSERVATOIRE_ALT0, pts: OBSERVATOIRE_PTS },
    goal: 'time', target: 24 * 3600, window: 36 * 3600, xp: 2000,
    credit: 'Road and trees © OpenStreetMap contributors (ODbL) · buildings: UrbIS 3D, Brussels Region (CC0) · elevation: Copernicus EU-DEM',
  },
];

export const findChallenge = (id: string | null | undefined): ChallengeDef | null => CHALLENGES.find(c => c.id === id) ?? null;
/** Challenge played on a track, if the track belongs to one. */
export const challengeForTrack = (trackId: string | null | undefined): ChallengeDef | null => CHALLENGES.find(c => c.track.id === trackId) ?? null;

/** Value of the goal reached by an attempt (or a ride). */
export const challengeValue = (def: ChallengeDef, a: ChallengeRide | null | undefined): number => (a ? a[def.goal] : 0);

/**
 * What a ride brings to a challenge, recomputed from the samples: elevation from the track's profile at each recorded
 * distance (the recorded altitude is ignored), riding time as moving seconds. A distance step faster than the speed bound
 * or going backwards adds nothing, so a forged distance series cannot rack up climbing.
 */
export function challengeRide(def: ChallengeDef, S: Samples): ChallengeRide {
  const tr = buildTrack(def.track), n = S.d.length;
  let climb = 0, distance = 0, time = 0;
  for (let i = 0; i < n; i++) if (S.v[i] >= 1) time++;
  for (let i = 1; i < n; i++) {
    const dd = S.d[i] - S.d[i - 1], dt = Math.max(1, S.t[i] - S.t[i - 1]);
    if (!(dd > 0 && dd <= 31 * dt)) continue;
    distance += dd;
    const up = altitudeAt(tr, S.d[i]) - altitudeAt(tr, S.d[i - 1]);
    if (up > 0) climb += up;
  }
  return { climb: Math.round(climb), time, distance: Math.round(distance) };
}

/** Attempt still open at `now` (window not yet over), or null. */
export function activeAttempt(def: ChallengeDef, rec: ChallengeRecord | null | undefined, now: number): ChallengeAttempt | null {
  const a = rec?.attempt;
  return a && now <= a.start + def.window * 1000 ? a : null;
}

/**
 * Adds a ride ending at `end` (ms) to a challenge record, without mutating it. A ride that starts after the window
 * opens a new attempt. Reaching the target closes the attempt: one more completion, best time kept.
 */
export function recordChallengeRide(def: ChallengeDef, rec: ChallengeRecord | null | undefined, ride: ChallengeRide, end: number): { record: ChallengeRecord; completed: boolean; restarted: boolean } {
  const prev: ChallengeRecord = rec ? structuredClone(rec) : { attempt: null, completions: 0, best: null };
  const start = end - ride.time * 1000;
  const open = prev.attempt && start <= prev.attempt.start + def.window * 1000 ? prev.attempt : null;
  const restarted = !!prev.attempt && !open;
  const a: ChallengeAttempt = open ?? { start, last: end, climb: 0, time: 0, distance: 0, rides: 0 };
  a.climb += ride.climb; a.time += ride.time; a.distance += ride.distance; a.rides++; a.last = Math.max(a.last, end);
  if (challengeValue(def, a) < def.target) return { record: { ...prev, attempt: a }, completed: false, restarted };
  const elapsed = Math.round((a.last - a.start) / 1000);
  return { record: { attempt: null, completions: prev.completions + 1, best: prev.best == null ? elapsed : Math.min(prev.best, elapsed) }, completed: true, restarted };
}
